import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { SvgIcon } from '@progress/kendo-react-common'
import clsx from 'clsx'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { useMediaQuery } from 'react-responsive'
import useMeasure from 'react-use-measure'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { COUNTRY_CODES } from '../../consts'
import { CustomerTaxCertificate } from '../../features/customer/customerAPI'
import {
    openCustomerTypeModal,
    openIdentifierUpdate,
    openShipToModify,
    openUniqueCustomerNameModal,
} from '../../features/customer/customerSlice'
import {
    CarrierServiceOptionsReturnPayload,
    FreightTermsReturnPayload,
    QuoteUserInfo,
    WarehouseReturnPayload,
} from '../../features/order/orderAPI'
import {
    downloadPDF,
    getOrderMiscInfo,
    saveOrder,
    setDateValidity,
    setReferenceNumber,
    setQuoteOpenNotes,
    setQuoteCloseNotes,
    setShipmentMethodDescription,
    ShippingInfo,
    updateShipmentMethodDescription,
    updateShippingInfo,
    updateValidUntilDate,
} from '../../features/order/orderSlice'
import { DatePicker } from '../../GlobalComponents/DateInput/DateInput'
import { TextAreaModal } from '../../GlobalComponents/GenericModals/GenericModals'
import { logEvent } from '../../GlobalComponents/GlobalFunctions'
import { EditPencil } from '../../GlobalComponents/_icons/Icons'
import {
    AWCButton,
    BUTTON_TYPES,
    FastQuoteDropdownList,
    FastQuoteInput,
    StyledRadioSingleChoice,
    ToggledInput,
} from '../../GlobalComponents/Inputs/Inputs'
import { TextOverflow } from '../../GlobalComponents/_misc/TextOverflow'
import { FastQuoteAttachmentModal } from './AttachmentModal'
import { HoverIcon } from '../../GlobalComponents/_icons/HoverIcon'
import { BOMField, getIconClass, RequirementsBubble } from './OrderScreen'
import styles from './OrderScreen.module.scss'
import { RequestQuoteChangesModal } from './RequestQuoteChangesModal'
import { SendQuoteModal } from './SendQuoteModal'
import { getCurrentUser } from '../../features/meta/metaSlice'

// import { fqAlert } from './NotificationManager'
const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

// Get lists for the dropdowns
const getCarriersList = (carrierServiceOptions: CarrierServiceOptionsReturnPayload) => {
    const carriersList: any = []
    carrierServiceOptions.forEach((carrier) => {
        const tempList: any = []
        tempList.push(carrier.CarrierKey)
        tempList.push(carrier.CarrierName)
        carriersList.push(tempList)
    })
    return carriersList
}

const getCarriersServiceList = (
    carrierServiceOptions: CarrierServiceOptionsReturnPayload,
    carrierKey: number
) => {
    const carriersServiceList: any = []
    carrierServiceOptions.forEach((carrier) => {
        if (carrier.CarrierKey === carrierKey) {
            carrier.CarrierServices.forEach((carrierService) => {
                const tempList: any = []
                tempList.push(carrierService.CarrierServiceKey)
                tempList.push(carrierService.ServiceName)
                tempList.push(carrierService.CarrierServiceDescription)
                carriersServiceList.push(tempList)
            })
        }
    })

    return carriersServiceList
}

const getCarriersTermsList = (freightTerms: FreightTermsReturnPayload) => {
    const carriersTermsList: any = []
    freightTerms.forEach((terms) => {
        const tempList: any = []
        tempList.push(terms.TermsKey)
        tempList.push(terms.ShortDescription)
        carriersTermsList.push(tempList)
    })
    return carriersTermsList
}

const getDefaultSmallPackageShippingInfo = (
    customer: CustomerRow,
    carrierServiceOptions: CarrierServiceOptionsReturnPayload,
    freightTerms: FreightTermsReturnPayload
) => {
    // Carrier
    const defaultSmallPackageKey = customer.NamedCarrierServiceKey
        ? parseInt(customer.NamedCarrierServiceKey.split(';')[0])
        : 2 // Default: UPS

    // Service
    const defaultSmallPackageServiceKey = customer.NamedCarrierServiceKey
        ? parseInt(customer.NamedCarrierServiceKey.split(';')[1])
        : 7 // Default: Ground

    const defaultSmallPackageTermsKey = customer.CarrierTermsKey ? customer.CarrierTermsKey : 4 // Default: PPA

    return {
        serviceKey: defaultSmallPackageServiceKey,
        carrierKey: defaultSmallPackageKey,
        termsKey: defaultSmallPackageTermsKey, // Default: PPA,
        collectAccount: customer.CarrierAccountCode ? customer.CarrierAccountCode : '',
    }
}
const getDefaultFreightShippingInfo = (
    customer: CustomerRow,
    carrierServiceOptions: CarrierServiceOptionsReturnPayload,
    freightTerms: FreightTermsReturnPayload
) => {
    const defaultFreightServiceKey = customer.NamedFreightCarrierServiceKey
        ? parseInt(customer.NamedFreightCarrierServiceKey.split(';')[1])
        : 70 // Default: Freight LTL

    const defaultFreightKey = customer.NamedFreightCarrierServiceKey
        ? parseInt(customer.NamedFreightCarrierServiceKey.split(';')[0])
        : 2 // Default: UPS

    // Terms
    const defaultFreightTermsKey = customer.FreightTermsKey ? customer.FreightTermsKey : 3 // Default: Collect
    return {
        serviceKey: defaultFreightServiceKey,
        carrierKey: defaultFreightKey,
        termsKey: defaultFreightTermsKey, // Default: PPA,
        collectAccount: customer.FreightAccountCode ? customer.FreightAccountCode : '',
    }
}

interface ShippingMethodModalProps {
    open: boolean
    close(): void
    shippingInfo: ShippingInfo
    carrierServiceOptions: CarrierServiceOptionsReturnPayload
    freightTerms: FreightTermsReturnPayload
}
const ShippingMethodModal = (props: ShippingMethodModalProps) => {
    const {
        open,
        close,
        // shippingInfo,
        carrierServiceOptions,
        freightTerms,
    } = props
    const currentCustomer = useAppSelector((state) => state.order.currentCustomer)!
    // const orderHeader = useAppSelector((state) => state.order.orderHeader)
    const classes = useStyles()
    const dispatch = useAppDispatch()

    const getEditMode = (shippingInfo: {
        serviceKey: number
        carrierKey: number
        termsKey: number
        collectAccount: string
    }): 'Small Package' | 'LTL' | 'Custom' => {
        let defaultFreightInfo = getDefaultFreightShippingInfo(
            currentCustomer,
            carrierServiceOptions,
            freightTerms
        )
        let defaultSmallPackageInfo = getDefaultSmallPackageShippingInfo(
            currentCustomer,
            carrierServiceOptions,
            freightTerms
        )
        let isSmallPackage = Object.keys(defaultSmallPackageInfo).every(
            // @ts-ignore
            (key) => defaultSmallPackageInfo[key] === shippingInfo[key]
        )
        let isFreight = Object.keys(defaultFreightInfo).every(
            // @ts-ignore
            (key) => defaultFreightInfo[key] === shippingInfo[key]
        )
        if (isSmallPackage) return 'Small Package'
        if (isFreight) return 'LTL'
        return 'Custom'
    }
    // Carrier + service options
    // Terms options

    // Carrier key, service key, terms key, carrier account code
    const [currentCarrierKey, setCurrentCarrierKey] = useState<number>(
        props.shippingInfo.carrierKey
    )
    // currentCustomer.NamedCarrierServiceKey
    //     ? parseInt(currentCustomer.NamedCarrierServiceKey.split(';')[0])
    //     : 2 // Default: UPS

    const [currentServiceKey, setCurrentServiceKey] = useState<number>(
        props.shippingInfo.serviceKey
    )
    // currentCustomer.NamedCarrierServiceKey
    //     ? parseInt(currentCustomer.NamedCarrierServiceKey.split(';')[1])
    //     : 7 // Default: Ground

    const [currentTermsKey, setCurrentTermsKey] = useState<number>(props.shippingInfo.termsKey)
    // currentCustomer.CarrierTermsKey ? currentCustomer.CarrierTermsKey : 4 // Default: PPA

    // const [currentAccountCode, setCurrentAccountCode] = useState<string>(
    //     currentCustomer.CarrierAccountCode ? currentCustomer.CarrierAccountCode : ''
    // )
    const [currentAccountCode, setCurrentAccountCode] = useState<string>(
        props.shippingInfo.collectAccount
    )
    const getFullDefaultFreightShippingInfo = (
        customer: CustomerRow,
        carrierServiceOptions: CarrierServiceOptionsReturnPayload,
        freightTerms: FreightTermsReturnPayload
    ) => {
        const defaultFreightServiceKey = customer.NamedFreightCarrierServiceKey
            ? parseInt(customer.NamedFreightCarrierServiceKey.split(';')[1])
            : 70 // Default: Freight LTL

        const defaultFreightKey = customer.NamedFreightCarrierServiceKey
            ? parseInt(customer.NamedFreightCarrierServiceKey.split(';')[0])
            : 2 // Default: UPS
        let defaultFreightCarrier = ''
        let carriersList = getCarriersList(carrierServiceOptions)
        carriersList.forEach((keyValue: any) => {
            let key: number = keyValue[0]
            let value: string = keyValue[1]

            if (key === defaultFreightKey) {
                defaultFreightCarrier = value
            }
        })
        let defaultFreightService = ''

        carrierServiceOptions!.forEach((carrier) => {
            if (carrier.CarrierKey === defaultFreightKey) {
                carrier.CarrierServices.forEach((carrierServices) => {
                    if (carrierServices.CarrierServiceKey === defaultFreightServiceKey) {
                        defaultFreightService = carrierServices.ServiceName
                    }
                })
            }
        })
        const defaultFreightTermsKey = customer.FreightTermsKey ? customer.FreightTermsKey : 3 // Default: Collect
        let defaultFreightTerms = ''
        freightTerms!.forEach((term) => {
            if (term.TermsKey === defaultFreightTermsKey) {
                defaultFreightTerms = term.ShortDescription
            }
        })
        return {
            serviceKey: defaultFreightServiceKey,
            service: defaultFreightService,
            carrierKey: defaultFreightKey,
            carrier: defaultFreightCarrier,
            terms: defaultFreightTerms,
            termsKey: defaultFreightTermsKey, // Default: PPA,
            collectAccount: customer.FreightAccountCode ? customer.FreightAccountCode : '',
        }
    }

    const getFullDefaultSmallPackageInfo = (
        customer: CustomerRow,
        carrierServiceOptions: CarrierServiceOptionsReturnPayload,
        freightTerms: FreightTermsReturnPayload
    ) => {
        // Carrier
        const defaultSmallPackageKey = customer.NamedCarrierServiceKey
            ? parseInt(customer.NamedCarrierServiceKey.split(';')[0])
            : 2 // Default: UPS
        let defaultSmallPackageCarrier = ''
        let carriersList = getCarriersList(carrierServiceOptions)
        carriersList.forEach((keyValue: any) => {
            let key: number = keyValue[0]
            let value: string = keyValue[1]

            if (key === defaultSmallPackageKey) {
                defaultSmallPackageCarrier = value
            }
        })

        // Service
        const defaultSmallPackageServiceKey = customer.NamedCarrierServiceKey
            ? parseInt(customer.NamedCarrierServiceKey.split(';')[1])
            : 7 // Default: Ground

        let defaultSmallPackageService = ''

        carrierServiceOptions!.forEach((carrier) => {
            if (carrier.CarrierKey === defaultSmallPackageKey) {
                carrier.CarrierServices.forEach((carrierServices) => {
                    if (carrierServices.CarrierServiceKey === defaultSmallPackageServiceKey) {
                        defaultSmallPackageService = carrierServices.ServiceName
                    }
                })
            }
        })

        const defaultSmallPackageTermsKey = customer.CarrierTermsKey ? customer.CarrierTermsKey : 4 // Default: PPA
        let defaultSmallPackageTerms = ''
        freightTerms!.forEach((term) => {
            if (term.TermsKey === defaultSmallPackageTermsKey) {
                defaultSmallPackageTerms = term.ShortDescription
            }
        })
        return {
            serviceKey: defaultSmallPackageServiceKey,
            service: defaultSmallPackageService,
            carrierKey: defaultSmallPackageKey,
            carrier: defaultSmallPackageCarrier,
            termsKey: defaultSmallPackageTermsKey, // Default: PPA,
            terms: defaultSmallPackageTerms,
            collectAccount: customer.CarrierAccountCode ? customer.CarrierAccountCode : '',
        }
    }

    let defaultFreightInfo = getFullDefaultFreightShippingInfo(
        currentCustomer,
        carrierServiceOptions,
        freightTerms
    )
    let defaultSmallPackageInfo = getFullDefaultSmallPackageInfo(
        currentCustomer,
        carrierServiceOptions,
        freightTerms
    )

    const editingShippingOption = getEditMode({
        carrierKey: currentCarrierKey,
        termsKey: currentTermsKey,
        serviceKey: currentServiceKey,
        collectAccount: currentAccountCode,
    })

    // Custom carrier key, service key, terms key, carrier account code
    const [customCarrierKey, setCustomCarrierKey] = useState<number>(
        editingShippingOption === 'Custom' ? props.shippingInfo.carrierKey : 0
    )
    const [customServiceKey, setCustomServiceKey] = useState<number>(
        editingShippingOption === 'Custom' ? props.shippingInfo.serviceKey : 0
    )
    const [customTermsKey, setCustomTermsKey] = useState<number>(
        editingShippingOption === 'Custom' ? props.shippingInfo.termsKey : 0
    )
    const [customAccountCode, setCustomAccountCode] = useState<string>(
        editingShippingOption === 'Custom' ? props.shippingInfo.collectAccount : ''
    )

    const carriersServiceList = getCarriersServiceList(carrierServiceOptions, customCarrierKey)
    const carriersList = getCarriersList(carrierServiceOptions)
    const carriersTermsList = getCarriersTermsList(freightTerms)

    // Boolean to whether we should show confirm only in terms dropdown
    const [showConfirmOnly, setShowConfirmOnly] = useState<boolean>(false)

    const handleCarrierKeyChange = (event: any) => {
        setCustomCarrierKey(event.target.value)
        setCurrentCarrierKey(event.target.value)
        setCustomServiceKey(0)
        setCurrentServiceKey(0)
        setCustomTermsKey(0)
        setCurrentTermsKey(0)
        setCustomAccountCode('')
        setCurrentAccountCode('')
    }

    const handleCarrierServiceKeyChange = (event: any) => {
        setCustomServiceKey(event.target.value)
        setCurrentServiceKey(event.target.value)
        // Anytime the service changes, blank out the terms
        setCustomTermsKey(0)
        setCurrentTermsKey(0)

        // 6;52 (52)
        // 6;115 (Service)
        // if carrier is seller and service is Do Not Ship or Service, show "Confirm Only" in terms, otherwise no "Confirm Only" in terms dropdown
        if (customCarrierKey === 6) {
            if (event.target.value === 52 || event.target.value === 115) {
                setShowConfirmOnly(true)
            } else {
                setShowConfirmOnly(false)
            }
        } else {
            setShowConfirmOnly(false)
        }
    }

    const handleCarrierTermsKeyChange = (event: any) => {
        setCustomTermsKey(event.target.value)
        setCurrentTermsKey(event.target.value)
        // if terms if collect or 3rd party, make sure account code is empty to begin with
        if (event.target.value === 3 || event.target.value === 5) {
            setCustomAccountCode('')
        }
    }

    const createShipmentMethodDescription = () => {
        let shippingMethodString = ''
        carrierServiceOptions!.forEach((carrier) => {
            if (carrier.CarrierKey === currentCarrierKey) {
                carrier.CarrierServices.forEach((carrierServices) => {
                    if (carrierServices.CarrierServiceKey === currentServiceKey) {
                        shippingMethodString += carrierServices.CarrierServiceValue + ';'
                    }
                })
            }
        })
        freightTerms!.forEach((term) => {
            if (term.TermsKey === currentTermsKey) {
                shippingMethodString += term.Terms
                if (term.Terms === 'Col' || term.Terms === '3rd') {
                    shippingMethodString += ';' + currentAccountCode
                }
            }
        })
        dispatch(updateShipmentMethodDescription({ ShipmentMethodString: shippingMethodString }))
    }

    // Set current as default small package
    const setCurrentAsDefaultSmallPackage = () => {
        setCurrentCarrierKey(
            currentCustomer.NamedCarrierServiceKey
                ? parseInt(currentCustomer.NamedCarrierServiceKey.split(';')[0])
                : 2 // Default: UPS
        )
        setCurrentServiceKey(
            currentCustomer.NamedCarrierServiceKey
                ? parseInt(currentCustomer.NamedCarrierServiceKey.split(';')[1])
                : 7 // Default: Ground
        )
        setCurrentTermsKey(
            currentCustomer.CarrierTermsKey ? currentCustomer.CarrierTermsKey : 4 // Default: PPA
        )
        setCurrentAccountCode(
            currentCustomer.CarrierAccountCode ? currentCustomer.CarrierAccountCode : ''
        )
    }

    // Set current as default freight
    const setCurrentAsDefaultFreight = () => {
        setCurrentCarrierKey(
            currentCustomer.NamedFreightCarrierServiceKey
                ? parseInt(currentCustomer.NamedFreightCarrierServiceKey.split(';')[0])
                : 2 // Default: UPS
        )
        setCurrentServiceKey(
            currentCustomer.NamedFreightCarrierServiceKey
                ? parseInt(currentCustomer.NamedFreightCarrierServiceKey.split(';')[1])
                : 70 // Default: Freight LTL
        )
        setCurrentTermsKey(
            currentCustomer.FreightTermsKey ? currentCustomer.FreightTermsKey : 3 // Default: Collect
        )
        setCurrentAccountCode(
            currentCustomer.FreightAccountCode ? currentCustomer.FreightAccountCode : ''
        )
    }

    const setCurrentAsCustom = () => {
        setCurrentCarrierKey(customCarrierKey)
        setCurrentServiceKey(customServiceKey)
        setCurrentTermsKey(customTermsKey)
        setCurrentAccountCode(customAccountCode)
    }

    return (
        <Modal
            open={open}
            className={classes.modal}
            onClose={() => {
                close()
            }}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modalContainer}>
                <div className={styles.shippingMethodModal}>
                    <div className={styles.shippingMethodModalHeader}>
                        <div>Shipping Method</div>
                        <div
                            onClick={() => {
                                close()
                            }}
                            className={'a-i-close'}
                        ></div>
                    </div>
                    <div className={'a-divider'} style={{ marginBottom: 0 }} />

                    <div
                        style={{ paddingTop: 15 }}
                        className={
                            editingShippingOption === 'Small Package'
                                ? styles.selectedShippingOption
                                : ''
                        }
                    >
                        <div
                            style={{
                                paddingBottom: 10,
                                display: 'flex',
                                gap: '3%',
                                alignItems: 'center',
                            }}
                            onClick={() => setCurrentAsDefaultSmallPackage()}
                        >
                            <div style={{ paddingLeft: '5%' }}>
                                <StyledRadioSingleChoice
                                    checked={editingShippingOption === 'Small Package'}
                                    value={editingShippingOption}
                                    onClick={() => setCurrentAsDefaultSmallPackage()}
                                    style={{ color: 'var(--linkblue)' }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    width: '20%',
                                    cursor: 'pointer',
                                }}
                            >
                                <div>Small</div>
                                <div>Package</div>
                            </div>
                            <div style={{ width: '80%' }}>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Carrier:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultSmallPackageInfo.carrier}
                                    </div>
                                </div>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Service:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultSmallPackageInfo.service}
                                    </div>
                                </div>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Terms:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultSmallPackageInfo.terms}
                                    </div>
                                </div>

                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Acct #:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultSmallPackageInfo.collectAccount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={'a-divider'} style={{ marginBottom: 0 }} />

                    <div
                        style={{ paddingTop: 15 }}
                        className={
                            editingShippingOption === 'LTL' ? styles.selectedShippingOption : ''
                        }
                    >
                        <div
                            style={{
                                paddingBottom: 15,
                                display: 'flex',
                                gap: '3%',
                                alignItems: 'center',
                            }}
                            onClick={() => setCurrentAsDefaultFreight()}
                        >
                            <div style={{ paddingLeft: '5%' }}>
                                <StyledRadioSingleChoice
                                    checked={editingShippingOption === 'LTL'}
                                    value={editingShippingOption}
                                    onClick={() => setCurrentAsDefaultFreight()}
                                    style={{ color: 'var(--linkblue)' }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    width: '20%',
                                    cursor: 'pointer',
                                }}
                            >
                                <div>LTL</div>
                            </div>
                            <div style={{ width: '80%' }}>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Carrier:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultFreightInfo.carrier}
                                    </div>
                                </div>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Service:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultFreightInfo.service}
                                    </div>
                                </div>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Terms:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultFreightInfo.terms}
                                    </div>
                                </div>
                                <div className={styles.shippingMethodModalLine}>
                                    <div className={styles.shippingMethodModalLabel}>
                                        Acct #:&nbsp;
                                    </div>
                                    <div style={{ fontWeight: 300 }}>
                                        {defaultFreightInfo.collectAccount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={'a-divider'} style={{ marginBottom: 0 }} />

                    <div
                        style={{ paddingTop: 15 }}
                        className={
                            editingShippingOption === 'Custom' ? styles.selectedShippingOption : ''
                        }
                    >
                        <div
                            style={{
                                paddingBottom: 15,
                                display: 'flex',
                                gap: '3%',
                                alignItems: 'center',
                            }}
                        >
                            <div style={{ paddingLeft: '5%' }}>
                                <StyledRadioSingleChoice
                                    checked={editingShippingOption === 'Custom'}
                                    value={editingShippingOption}
                                    onChange={() => setCurrentAsCustom()}
                                    style={{ color: 'var(--linkblue)' }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    width: '20%',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setCurrentAsCustom()}
                            >
                                <div>Custom</div>
                            </div>
                            <div style={{ width: '80%' }}>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr',
                                        gap: 15,
                                        marginTop: 10,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className={styles.shippingMethodModalTextBoxLabel}>
                                            Carrier:&nbsp;
                                        </div>
                                        <div style={{ width: '70%' }}>
                                            <FastQuoteDropdownList
                                                title={'Carrier'}
                                                value={customCarrierKey}
                                                selectList={carriersList}
                                                onChange={handleCarrierKeyChange}
                                                editingShippingOption={editingShippingOption}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className={styles.shippingMethodModalTextBoxLabel}>
                                            Service:&nbsp;
                                        </div>
                                        <div style={{ width: '70%' }}>
                                            <FastQuoteDropdownList
                                                title={'Service'}
                                                value={customServiceKey}
                                                selectList={carriersServiceList}
                                                onChange={handleCarrierServiceKeyChange}
                                                editingShippingOption={editingShippingOption}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div className={styles.shippingMethodModalTextBoxLabel}>
                                            Terms:&nbsp;
                                        </div>
                                        <div style={{ width: '70%' }}>
                                            <FastQuoteDropdownList
                                                title={'Terms'}
                                                value={customTermsKey}
                                                selectList={carriersTermsList}
                                                onChange={handleCarrierTermsKeyChange}
                                                showConfirmOnly={showConfirmOnly}
                                                editingShippingOption={editingShippingOption}
                                            />
                                        </div>
                                    </div>

                                    {customTermsKey === 3 || customTermsKey === 5 ? (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div className={styles.shippingMethodModalTextBoxLabel}>
                                                Acct #:&nbsp;
                                            </div>
                                            <div style={{ width: '70%' }}>
                                                <FastQuoteInput
                                                    value={customAccountCode}
                                                    onChange={(value: string) => {
                                                        setCustomAccountCode(value)
                                                        setCurrentAccountCode(value)
                                                    }}
                                                    editingShippingOption={editingShippingOption}
                                                    maxLength={10}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <div
                        style={{
                            display: 'flex',
                            marginTop: 10,
                            justifyContent: 'center',
                        }}
                    >
                        <FastQuoteButton
                            type={
                                editingShippingOption === 'Custom'
                                    ? customCarrierKey !== 0 &&
                                      customServiceKey !== 0 &&
                                      customTermsKey !== 0
                                        ? BUTTON_TYPES.PRIMARY
                                        : BUTTON_TYPES.DISABLED
                                    : BUTTON_TYPES.PRIMARY
                            }
                            onClick={
                                editingShippingOption === 'Custom'
                                    ? customCarrierKey !== 0 &&
                                      customServiceKey !== 0 &&
                                      customTermsKey !== 0
                                        ? () => {
                                              dispatch(
                                                  updateShippingInfo({
                                                      carrierKey: currentCarrierKey,
                                                      termsKey: currentTermsKey,
                                                      serviceKey: currentServiceKey,
                                                      collectAccount: currentAccountCode,
                                                  })
                                              )
                                              createShipmentMethodDescription()
                                              dispatch(getOrderMiscInfo(false))
                                              close()
                                          }
                                        : () => {}
                                    : () => {
                                          dispatch(
                                              updateShippingInfo({
                                                  carrierKey: currentCarrierKey,
                                                  termsKey: currentTermsKey,
                                                  serviceKey: currentServiceKey,
                                                  collectAccount: currentAccountCode,
                                              })
                                          )
                                          createShipmentMethodDescription()
                                          dispatch(getOrderMiscInfo(false))
                                          close()
                                      }
                            }
                            style={{ width: 450 }}
                        >
                            Save
                        </FastQuoteButton>
                    </div> */}
                    <div
                        style={{
                            justifyContent: 'flex-end',
                            display: 'grid',
                            gridTemplateColumns: '110px 110px',
                            marginTop: 10,
                        }}
                    >
                        <AWCButton
                            type={'secondary'}
                            style={{
                                width: 94,
                            }}
                            onClick={() => close()}
                        >
                            Cancel
                        </AWCButton>
                        <AWCButton
                            type={
                                editingShippingOption === 'Custom'
                                    ? customCarrierKey !== 0 &&
                                      customServiceKey !== 0 &&
                                      customTermsKey !== 0
                                        ? BUTTON_TYPES.PRIMARY
                                        : BUTTON_TYPES.DISABLED
                                    : BUTTON_TYPES.PRIMARY
                            }
                            onClick={
                                editingShippingOption === 'Custom'
                                    ? customCarrierKey !== 0 &&
                                      customServiceKey !== 0 &&
                                      customTermsKey !== 0
                                        ? () => {
                                              dispatch(
                                                  updateShippingInfo({
                                                      carrierKey: currentCarrierKey,
                                                      termsKey: currentTermsKey,
                                                      serviceKey: currentServiceKey,
                                                      collectAccount: currentAccountCode,
                                                  })
                                              )
                                              createShipmentMethodDescription()
                                              dispatch(getOrderMiscInfo(false))
                                              close()
                                          }
                                        : () => {}
                                    : () => {
                                          dispatch(
                                              updateShippingInfo({
                                                  carrierKey: currentCarrierKey,
                                                  termsKey: currentTermsKey,
                                                  serviceKey: currentServiceKey,
                                                  collectAccount: currentAccountCode,
                                              })
                                          )
                                          createShipmentMethodDescription()
                                          dispatch(getOrderMiscInfo(false))
                                          close()
                                      }
                            }
                            style={{ width: 94 }}
                        >
                            Save
                        </AWCButton>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
interface SalesPersonHoverProps {
    SalesPersonAssignments: Array<{
        SalesPerson: string
        SalesPersonName: string
        LineItems: string[]
    }>
    onLineClick(line: string): void
}
const SalesPersonHover = (props: SalesPersonHoverProps) => {
    const { SalesPersonAssignments, onLineClick } = props
    return (
        <div className={styles.identifierBubble} style={{ width: 200 }}>
            {SalesPersonAssignments.map((row, j) => (
                <>
                    <div
                        style={{
                            fontWeight: 370,
                            marginBottom: 5,
                        }}
                    >
                        {row.SalesPersonName}
                    </div>
                    <div>
                        Line Items{' '}
                        {row.LineItems.map((line, i) => (
                            <span>
                                <AWCButton
                                    type={'link'}
                                    style={{ display: 'inline' }}
                                    onClick={(e) => {
                                        onLineClick(line)
                                        // don't want this to have the 'click to stay open behavior
                                        e.stopPropagation()
                                    }}
                                >
                                    {line}
                                </AWCButton>
                                {i !== row.LineItems.length - 1 && ', '}
                            </span>
                        ))}
                    </div>
                    {j !== SalesPersonAssignments.length - 1 && (
                        <div
                            className='a-divider'
                            style={{
                                marginTop: 5,
                                marginBottom: 5,
                            }}
                        />
                    )}
                </>
            ))}
        </div>
    )
}
interface OrderScreenHeaderFastQuoteProps {
    hasValidPart: boolean
    orderMarginPercentage: number
    carrierServiceOptions?: CarrierServiceOptionsReturnPayload
    freightTerms?: FreightTermsReturnPayload
    setReviewingQuoteOutput: Dispatch<SetStateAction<boolean>>
    reviewingQuoteOutput: boolean
    lineItemsRef: any
    currentTaxCertificates?: CustomerTaxCertificate[] | null
    expiredTaxCertificates?: CustomerTaxCertificate[] | null
    openBOMModal: () => void
}

export const OrderScreenHeaderFastQuote = (props: OrderScreenHeaderFastQuoteProps) => {
    const {
        hasValidPart,
        orderMarginPercentage,
        lineItemsRef,
        currentTaxCertificates,
        expiredTaxCertificates,
        openBOMModal,
    } = props

    // const isNewOrder = useAppSelector(isNewOrderSelector)
    let orderShipTo = useAppSelector((state) => state.order.currentShipTo)
    let freightTerms = useAppSelector((state) => state.order.freightTerms)
    let carrierServiceOptions = useAppSelector((state) => state.order.carrierServiceOptions)
    let customer = useAppSelector((state) => state.customer.currentCustomer!)
    let currentAccount = useAppSelector((state) => state.order.currentAccount!)

    const previewMode = useAppSelector((state) => state.meta.previewMode)

    const orderHeader = useAppSelector((state) => state.order.orderHeader)
    const orderMiscInfo = useAppSelector((state) => state.order.orderMiscInfo!)
    const dispatch = useAppDispatch()

    // Screen size resolution
    const smallScreen = useMediaQuery({ query: '(max-width: 1484px)' })

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })

    const [attachmentModalOpen, setAttachmentModalOpen] = useState(false)
    const [quoteOpenNotesModalOpen, setQuoteOpenNotesModalOpen] = useState<boolean>(false)
    const [quoteCloseNotesModalOpen, setQuoteCloseNotesModalOpen] = useState<boolean>(false)

    const shippingInfo = useAppSelector((state) => state.order.shippingInfo)
    const freightTermsShort = useAppSelector(
        (state) =>
            state.order.freightTerms?.find((row) => row.TermsKey === shippingInfo.termsKey)?.Terms
    )

    const {
        carrier: shippingCarrier,
        service: shippingService,
        terms: shippingTerms,
        collectAccount: shippingCollectAccount,
    } = shippingInfo

    useEffect(() => {
        const shipmentMethodString =
            shippingInfo.carrier + ';' + shippingInfo.service + ';' + freightTermsShort
        // updateShipmentMethodDescription({ ShipmentMethodString: shipmentMethodString })
        dispatch(setShipmentMethodDescription(shipmentMethodString))
    }, [shippingInfo])

    return (
        <div className={smallScreen ? styles.smallScreenHeader : styles.screenHeader}>
            <div
                className={
                    smallScreen ? styles.smallOrderHeaderContainer : styles.orderHeaderContainer
                }
            >
                <div className={styles.billToSection} data-testid='billToSection'>
                    <div
                        style={{
                            paddingBottom: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                            <span>{customer.CustomerName || customer.BillToName}</span>
                        </div>
                        <div
                            className={styles.sectionAction}
                            onClick={() => {
                                logEvent({
                                    eventName: 'Unique Cust Edit Quote Entry',
                                    string2: customer.AccountCode,
                                })
                                dispatch(openUniqueCustomerNameModal(customer))
                            }}
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </div>
                    </div>
                    <div
                        style={{
                            paddingBottom: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                            <span>Account Identifier: </span>
                            <span>{customer.ReasonName || 'Standard'}</span>
                        </div>
                        <div
                            className={styles.sectionAction}
                            onClick={() => {
                                logEvent({
                                    eventName: 'Identifier Edit Quote Entry',
                                    string2: customer.AccountCode,
                                })

                                dispatch(openIdentifierUpdate(customer))
                            }}
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </div>
                    </div>
                    <div
                        style={{
                            marginBottom: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                            height: 45,
                        }}
                    >
                        <div style={{ display: 'flex' }}>
                            <span style={{ minWidth: 100 }}>Customer Type: </span>
                            <TextOverflow text={customer.CustomerTypeName || ''} maxWidth={200} />
                        </div>

                        <div
                            className={styles.sectionAction}
                            onClick={() => {
                                logEvent({
                                    eventName: 'Customer Type Edit Quote Entry',
                                    string2: customer.AccountCode,
                                })
                                dispatch(openCustomerTypeModal(customer))
                            }}
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </div>
                    </div>

                    <div className={styles.billToRow}>
                        <div
                            className={isLargeView ? styles.lightTextLarge : styles.lightTextSmall}
                        >
                            Bill to:
                        </div>
                    </div>
                    <div className={styles.customerLine}>
                        {orderHeader[0].BillToName || currentAccount.BillToName} (
                        {orderHeader[0].CustomerAccountCode.trim()})
                    </div>
                    <div className={styles.billToLine}>
                        {orderHeader[0].BillToAddress1 || currentAccount.BillToAddress1}
                    </div>
                    <div className={styles.billToLine}>
                        {orderHeader[0].BillToAddress2 || currentAccount.BillToAddress2}
                    </div>
                    <div className={styles.billToLine}>
                        {orderHeader[0].BillToCity || currentAccount.BillToCity}
                        {orderHeader[0].BillToCity || (currentAccount.BillToCity && ',')}{' '}
                        {orderHeader[0].BillToState || currentAccount.BillToState}{' '}
                        {orderHeader[0].BillToZipcode || currentAccount.BillToZipcode}
                    </div>
                    <div className={styles.billToLine} style={{ paddingBottom: 10 }}>
                        {orderHeader[0].BillToCountry
                            ? COUNTRY_CODES[orderHeader[0].BillToCountry.trim()]
                            : currentAccount.BillToCountry
                            ? COUNTRY_CODES[currentAccount.BillToCountry.trim()]
                            : ''}
                    </div>
                </div>
                <div className={styles.shipToSection} data-testid='shipToSection'>
                    <div
                        className={styles.shipToRow}
                        style={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                        <div
                            className={isLargeView ? styles.lightTextLarge : styles.lightTextSmall}
                        >
                            Ship to:
                        </div>
                        <div style={{ marginRight: 10 }}>
                            <AWCButton
                                type={BUTTON_TYPES.LINK}
                                onClick={() => {
                                    dispatch(openShipToModify(customer))
                                }}
                                data-testid='customerAccountShiptoEdit'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                }}
                            >
                                <SvgIcon icon={EditPencil} height={15} />
                            </AWCButton>
                        </div>
                    </div>
                    <div className={styles.customerLine}>{orderShipTo?.ShipToName || ''}</div>
                    <div className={styles.billToLine}>{orderShipTo?.ShipToAddress1 || ''}</div>
                    <div className={styles.billToLine}>{orderShipTo?.ShipToAddress2 || ''}</div>
                    <div className={styles.billToLine}>
                        {orderShipTo?.ShipToCity || ''}
                        {orderShipTo?.ShipToCity && ','} {orderShipTo?.ShipToState?.trim() || ''}
                        <span style={{ marginLeft: 4 }}>{orderShipTo?.ShipToZipcode || ''}</span>
                    </div>
                    <div className={styles.billToLine}>
                        {
                            COUNTRY_CODES[
                                orderShipTo?.ShipToCountry ? orderShipTo.ShipToCountry.trim() : ''
                            ]
                        }
                    </div>

                    <div
                        className={isLargeView ? styles.lightTextLarge : styles.lightTextSmall}
                        style={{ marginTop: 30, marginBottom: 10 }}
                    >
                        Tax Certificate:
                    </div>

                    {(currentTaxCertificates && currentTaxCertificates.length > 0) ||
                    (expiredTaxCertificates && expiredTaxCertificates.length > 0) ? (
                        <>
                            <div className={styles.customerLine}>
                                {currentTaxCertificates && currentTaxCertificates?.length > 0 ? (
                                    <HoverIcon
                                        growLeft={false}
                                        growDown={true}
                                        renderIcon={() => (
                                            <span className={styles.taxCertificateNumber}>
                                                Current ({currentTaxCertificates?.length})
                                            </span>
                                        )}
                                    >
                                        {(closeBubble: () => void) => (
                                            <div
                                                className={styles.taxBubble}
                                                style={{
                                                    height: '100%',
                                                    textAlign: 'left',
                                                    whiteSpace: 'nowrap',
                                                    padding: '14px 19px',
                                                }}
                                                onClick={(e: any) => e.stopPropagation()}
                                            >
                                                <div>
                                                    <span
                                                        style={{
                                                            fontWeight: 400,
                                                            marginRight: 12,
                                                            marginBottom: 15,
                                                        }}
                                                    >
                                                        Avalara Stored Exemption Certificates
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns:
                                                            '110px 110px 120px 135px 135px 130px',
                                                        fontWeight: 500,
                                                        justifyItems: 'center',
                                                        marginTop: 10,
                                                    }}
                                                >
                                                    <div>Cert Tax ID</div>
                                                    <div>Exposure Zone</div>
                                                    <div>Exempt Reason</div>
                                                    <div>Certificate Eff Date</div>
                                                    <div>Certificate Exp Date</div>
                                                    <div>PO# (1 Time Use)</div>
                                                </div>

                                                <div
                                                    className='a-divider'
                                                    style={{
                                                        marginTop: 10,
                                                        marginBottom: 10,
                                                    }}
                                                />

                                                <Scrollbars
                                                    className={'scroll-bar'}
                                                    renderTrackHorizontal={() => <div />}
                                                    autoHeight
                                                    autoHeightMin={0}
                                                    autoHeightMax={210}
                                                    // height={210}
                                                >
                                                    <>
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    '120px 110px 100px 150px 130px 125px',
                                                                paddingRight: 15,
                                                            }}
                                                        >
                                                            {currentTaxCertificates.map(
                                                                (row: CustomerTaxCertificate) => (
                                                                    <>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.TaxID}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.ExposureZone}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {row.ExemptReason}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                row.EffectiveDate
                                                                            ).toLocaleDateString(
                                                                                'en-US',
                                                                                {
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    year: 'numeric',
                                                                                }
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                row.ExpirationDate
                                                                            ).toLocaleDateString(
                                                                                'en-US',
                                                                                {
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    year: 'numeric',
                                                                                }
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.PONumber}
                                                                        </div>
                                                                    </>
                                                                )
                                                            )}
                                                        </div>
                                                    </>
                                                </Scrollbars>
                                            </div>
                                        )}
                                    </HoverIcon>
                                ) : (
                                    <span>Current (0)</span>
                                )}
                            </div>
                            <div className={styles.customerLine}>
                                {expiredTaxCertificates && expiredTaxCertificates?.length > 0 ? (
                                    <HoverIcon
                                        growLeft={false}
                                        growDown={true}
                                        renderIcon={() => (
                                            <span className={styles.taxCertificateNumber}>
                                                Expired ({expiredTaxCertificates?.length})
                                            </span>
                                        )}
                                    >
                                        {(closeBubble: () => void) => (
                                            <div
                                                className={styles.taxBubble}
                                                style={{
                                                    height: '100%',
                                                    textAlign: 'left',
                                                    whiteSpace: 'nowrap',
                                                    padding: '14px 19px',
                                                }}
                                                onClick={(e: any) => e.stopPropagation()}
                                            >
                                                <div>
                                                    <span
                                                        style={{
                                                            fontWeight: 400,
                                                            marginRight: 12,
                                                            marginBottom: 15,
                                                        }}
                                                    >
                                                        Avalara Stored Exemption Certificates
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns:
                                                            '110px 110px 120px 135px 135px 130px',
                                                        fontWeight: 500,
                                                        justifyItems: 'center',
                                                        marginTop: 10,
                                                    }}
                                                >
                                                    <div>Cert Tax ID</div>
                                                    <div>Exposure Zone</div>
                                                    <div>Exempt Reason</div>
                                                    <div>Certificate Eff Date</div>
                                                    <div>Certificate Exp Date</div>
                                                    <div>PO# (1 Time Use)</div>
                                                </div>

                                                <div
                                                    className='a-divider'
                                                    style={{
                                                        marginTop: 10,
                                                        marginBottom: 10,
                                                    }}
                                                />

                                                <Scrollbars
                                                    className={'scroll-bar'}
                                                    renderTrackHorizontal={() => <div />}
                                                    autoHeight
                                                    autoHeightMin={0}
                                                    autoHeightMax={210}
                                                    // height={210}
                                                >
                                                    <>
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    '120px 110px 100px 150px 130px 125px',
                                                                paddingRight: 15,
                                                            }}
                                                        >
                                                            {expiredTaxCertificates.map(
                                                                (row: CustomerTaxCertificate) => (
                                                                    <>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.TaxID}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.ExposureZone}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {row.ExemptReason}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                row.EffectiveDate
                                                                            ).toLocaleDateString(
                                                                                'en-US',
                                                                                {
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    year: 'numeric',
                                                                                }
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'center',
                                                                            }}
                                                                        >
                                                                            {new Date(
                                                                                row.ExpirationDate
                                                                            ).toLocaleDateString(
                                                                                'en-US',
                                                                                {
                                                                                    month: '2-digit',
                                                                                    day: '2-digit',
                                                                                    year: 'numeric',
                                                                                }
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                textAlign: 'start',
                                                                            }}
                                                                        >
                                                                            {row.PONumber}
                                                                        </div>
                                                                    </>
                                                                )
                                                            )}
                                                        </div>
                                                    </>
                                                </Scrollbars>
                                            </div>
                                        )}
                                    </HoverIcon>
                                ) : (
                                    <span>Expired (0)</span>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className={styles.customerLine}>Not on File</div>
                    )}
                </div>
                {smallScreen && (
                    <div
                        style={{
                            gridRow: '2/3',
                            gridColumn: '1/3',
                            border: '1px solid var(--border1)',
                            borderLeftWidth: 0,
                            borderBottomWidth: 0,
                            display: 'flex',
                        }}
                    >
                        <div
                            style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8 }}
                        >
                            <AWCButton
                                type={BUTTON_TYPES.LINK}
                                onClick={openBOMModal}
                                style={{ display: 'flex', marginRight: 50 }}
                            >
                                <div>Import BOM</div>
                            </AWCButton>
                        </div>
                        <BOMField />
                    </div>
                )}
                <div
                    style={
                        smallScreen
                            ? {
                                  gridRow: '1/3',
                              }
                            : {}
                    }
                >
                    <div className={styles.quoteMetaInfo}>
                        <div>Customer Ref:</div>
                        <div data-testid='customerReferenceNumber'>
                            <ToggledInput
                                value={orderHeader[0].RFQNumber}
                                onChange={(newVal) => dispatch(setReferenceNumber(newVal))}
                                editTestId={'editCustomerReferenceNumber'}
                                doneTestId={'doneCustomerReferenceNumber'}
                            />
                        </div>
                        <div>Quote #:</div>
                        <div
                            className={isLargeView ? styles.lightTextLarge : styles.lightTextSmall}
                            data-testid='quoteNumber'
                        >
                            {orderHeader[0].OrderNumber === '9999999999'
                                ? 'New'
                                : orderHeader[0].OrderNumber}
                        </div>
                        <div>Quote Version:</div>
                        <div
                            className={isLargeView ? styles.lightTextLarge : styles.lightTextSmall}
                            data-testid='quoteVersion'
                        >
                            {(orderHeader[0].Revision || 0).toString().padStart(3, '0')}
                        </div>
                        <div>
                            Quote Notes:
                            {orderHeader[0].QuoteOpenNotes && (
                                <HoverIcon
                                    renderIcon={() => (
                                        <span
                                            className='a-i-info'
                                            style={{ marginTop: 5, marginLeft: '5px' }}
                                        />
                                    )}
                                >
                                    {(closeBubble: () => void) => (
                                        <div style={{ width: '500px' }}>
                                            <div
                                                className={`${styles.identifierBubble} ${styles.notesBubble}`}
                                            >
                                                {orderHeader[0].QuoteOpenNotes!}
                                            </div>
                                        </div>
                                    )}
                                </HoverIcon>
                            )}
                        </div>

                        <AWCButton
                            type={BUTTON_TYPES.LINK}
                            onClick={() => {
                                setQuoteOpenNotesModalOpen(true)
                            }}
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </AWCButton>

                        {/* <div
                            style={{
                                display: 'flex',
                                // justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                         <span style={{ marginLeft: '20px' }}>Close</span>
                            <AWCButton
                                type={BUTTON_TYPES.LINK}
                                onClick={() => {
                                    setQuoteCloseNotesModalOpen(true)
                                }}
                            >
                                <div className={styles.sectionAction} style={{ paddingLeft: 5 }}>
                                    <SvgIcon icon={EditPencil}  height={15} />
                                </div>
                            </AWCButton>
                            {orderHeader[0].QuoteCloseNotes && (
                                <HoverIcon
                                    renderIcon={() => (
                                        <span
                                            className="a-i-info"
                                            style={{ marginTop: 5, marginLeft: '5px' }}
                                        />
                                    )}
                                >
                                    {(closeBubble: () => void) => (
                                        <div style={{ width: 500 }}>
                                            <div
                                                className={styles.identifierBubble}
                                                style={{
                                                    wordBreak: 'break-all',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: 'fit-content',
                                                }}
                                            >
                                                {orderHeader[0].QuoteCloseNotes!}
                                            </div>
                                        </div>
                                    )}
                                </HoverIcon>
                            )}
                        </div> */}
                        <TextAreaModal
                            title={'Quote Open Notes'}
                            close={() => {
                                setQuoteOpenNotesModalOpen(false)
                            }}
                            open={quoteOpenNotesModalOpen}
                            onSubmit={(newVal) => dispatch(setQuoteOpenNotes(newVal))}
                            defaultValue={orderHeader[0].QuoteOpenNotes}
                        />
                        {/* <TextAreaModal
                            title={`Quote Close Notes`}
                            close={() => {
                                setQuoteCloseNotesModalOpen(false)
                            }}
                            open={quoteCloseNotesModalOpen}
                            onSubmit={(newVal) => dispatch(setQuoteCloseNotes(newVal))}
                            defaultValue={orderHeader[0].QuoteCloseNotes}
                        /> */}
                        <div>Valid Until:</div>
                        <div
                            style={{ whiteSpace: 'nowrap', position: 'relative' }}
                            data-testid='validUntilDate'
                        >
                            <form
                                className='k-form'
                                id='valid-until'
                                style={{ display: 'flex', alignItems: 'center', fontWeight: 300 }}
                            >
                                <div style={{ width: 150 }}>
                                    <DatePicker
                                        minDate={new Date()}
                                        onChange={(date) => dispatch(updateValidUntilDate(date))}
                                        value={orderHeader[0].QuoteExpirationDate}
                                        onValidityChange={(valid) =>
                                            dispatch(setDateValidity(valid))
                                        }
                                        required
                                        format='long'
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className={'a-divider'} style={{ marginTop: 24, marginBottom: 5 }} />
                    {/* todo pull up */}
                    <div style={{ fontWeight: 500, lineHeight: '20px' }}>
                        <div>
                            Requirements
                            <HoverIcon
                                growDown={true}
                                renderIcon={() => (
                                    <div
                                        className={styles.infoIcon}
                                        data-testid='requirementsIcon'
                                    />
                                )}
                            >
                                {(closeBubble: () => void) => (
                                    <RequirementsBubble accountCode={customer.AccountCode} />
                                )}
                            </HoverIcon>
                        </div>
                        <div>
                            <div>
                                Salesperson Assignments
                                {orderMiscInfo &&
                                    orderMiscInfo.SalesPersonAssignments &&
                                    orderMiscInfo.SalesPersonAssignments.length > 1 && (
                                        <HoverIcon
                                            renderIcon={() => <div className={styles.infoIcon} />}
                                        >
                                            {(closeBubble: () => void) => (
                                                <SalesPersonHover
                                                    SalesPersonAssignments={
                                                        orderMiscInfo.SalesPersonAssignments!
                                                    }
                                                    onLineClick={(line) =>
                                                        lineItemsRef.current?.focusLine(line)
                                                    }
                                                />
                                            )}
                                        </HoverIcon>
                                    )}
                            </div>
                            {orderMiscInfo && orderMiscInfo.SalesPersonAssignments && (
                                <div style={{ fontWeight: 300, marginLeft: 20 }}>
                                    {orderMiscInfo.SalesPersonAssignments.map(
                                        (row) => row.SalesPersonName
                                    ).join(', ')}
                                </div>
                            )}
                        </div>
                        {orderMiscInfo && (
                            <div>
                                <div>
                                    Credit Status
                                    <HoverIcon
                                        renderIcon={() => (
                                            <div
                                                className={getIconClass(
                                                    orderMiscInfo.CreditStatus!.Color
                                                )}
                                            />
                                        )}
                                    >
                                        {(closeBubble: () => void) => (
                                            <div className={styles.identifierBubble}>
                                                {orderMiscInfo.CreditStatus!.Caption}
                                                {orderMiscInfo.CreditStatus!.Caption && (
                                                    <div className='a-divider' />
                                                )}
                                                <div className={styles.creditStatusBubble}>
                                                    <div>Credit Limit:</div>
                                                    <div>
                                                        {orderMiscInfo.CreditStatus!.CreditLimit.toLocaleString(
                                                            'en-US',
                                                            {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }
                                                        )}{' '}
                                                        {orderMiscInfo.CreditStatus!.OverLimit && (
                                                            <span style={{ color: 'red' }}>
                                                                (Over Limit)
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>AR Rep:</div>
                                                    <div>
                                                        {orderMiscInfo.CreditStatus!.ARRep.FullName}
                                                    </div>
                                                    <div>AR Balance:</div>
                                                    <div>
                                                        {orderMiscInfo.CreditStatus!.ARBalance.toLocaleString(
                                                            'en-US',
                                                            {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }
                                                        )}
                                                    </div>
                                                    <div>Backlog Balance:</div>
                                                    <div>
                                                        {orderMiscInfo.CreditStatus!.BacklogBalance.toLocaleString(
                                                            'en-US',
                                                            {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }
                                                        )}
                                                    </div>
                                                    <div>Terms:</div>
                                                    <div>{orderMiscInfo.CreditStatus!.Terms}</div>
                                                    <div>Tax Status:</div>
                                                    <div>
                                                        {orderMiscInfo.CreditStatus!.IsTaxExempt
                                                            ? 'Exempt'
                                                            : 'Not Exempt'}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </HoverIcon>
                                </div>

                                <div style={{ fontWeight: 300, marginLeft: 20 }}>
                                    {orderMiscInfo.CreditStatus!.SalesAction}
                                </div>
                                <div></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                className={clsx([
                    !hasValidPart && styles.fadedText,
                    smallScreen ? styles.smallQuoteReviewContainer : styles.quoteReviewContainer,
                ])}
            >
                {!previewMode && <ShippingMethodInfo allowEdit={true} isLargeView={isLargeView} />}
                <div
                    className={styles.divider}
                    style={{ margin: isLargeView ? '30% 0px 0px 0px' : '30% 0px 0px 0px' }}
                />
                <div
                    style={{
                        marginBottom: '1%',
                        fontSize: isLargeView ? 22 : 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '5%',
                        fontWeight: 500,
                    }}
                >
                    Subtotal:{' '}
                    <span>
                        {orderMiscInfo && hasValidPart
                            ? orderMiscInfo.OrderValue.SubTotal.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                              })
                            : '$0.00'}
                    </span>
                </div>
                <div
                    className={styles.orderMargin}
                    style={{
                        fontSize: isLargeView ? 18 : 16,
                    }}
                >
                    Margin{' '}
                    <span style={{ fontWeight: 600, color: '#00be07' }}>
                        {orderMiscInfo && hasValidPart ? orderMarginPercentage : '0'}%
                    </span>
                </div>
            </div>
            <FastQuoteAttachmentModal
                open={attachmentModalOpen}
                close={() => setAttachmentModalOpen(false)}
            />
        </div>
    )
}

interface ShippingMethodInfoProps {
    allowEdit: boolean
    isLargeView: boolean
}

const ShippingMethodInfo = (props: ShippingMethodInfoProps) => {
    const { allowEdit, isLargeView } = props

    const [showShippingMethodModal, setShowShippingMethodModal] = useState<boolean>(false)

    const shippingInfo = useAppSelector((state) => state.order.shippingInfo)
    const freightTerms = useAppSelector((state) => state.order.freightTerms)
    const carrierServiceOptions = useAppSelector((state) => state.order.carrierServiceOptions)

    const {
        carrier: shippingCarrier,
        service: shippingService,
        terms: shippingTerms,
        collectAccount: shippingCollectAccount,
    } = shippingInfo

    return (
        <div
            className={styles.shippingMethod}
            // style={{
            //     marginBottom: '4%',
            //     // visibility: hasValidPart ? 'visible' : 'hidden',
            // }}
        >
            {carrierServiceOptions && freightTerms && showShippingMethodModal && (
                <ShippingMethodModal
                    open={showShippingMethodModal}
                    close={() => setShowShippingMethodModal(false)}
                    shippingInfo={shippingInfo}
                    carrierServiceOptions={carrierServiceOptions}
                    freightTerms={freightTerms}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <div className={styles.shippingMethodLabel}>Shipping Method:</div>
                {allowEdit && (
                    <div style={{ marginRight: 10 }}>
                        <AWCButton
                            type={BUTTON_TYPES.LINK}
                            onClick={() => {
                                // dispatch(openShipToModify(customer))
                                setShowShippingMethodModal(true)
                            }}
                            style={{
                                display: 'flex',
                                justifyContent: 'end',
                                fontSize: isLargeView ? 16 : 14,
                            }}
                        >
                            <SvgIcon icon={EditPencil} height={15} />
                        </AWCButton>
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                <div className={styles.shippingMethodLabel}>Carrier:&nbsp;</div>
                <div className={styles.shippingMethodValue}>{shippingCarrier}</div>
            </div>
            <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                <div className={styles.shippingMethodLabel}>Service:&nbsp;</div>
                <div className={styles.shippingMethodValue}>{shippingService}</div>
            </div>
            <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                <div className={styles.shippingMethodLabel}>Terms:&nbsp;</div>
                <div className={styles.shippingMethodValue}>{shippingTerms}</div>
            </div>
            {shippingCollectAccount !== '' && (
                <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                    <div className={styles.shippingMethodLabel}>Account #:&nbsp;</div>
                    <div className={styles.shippingMethodValue}>{shippingCollectAccount}</div>
                </div>
            )}
        </div>
    )
}

interface OrderScreenHeaderReviewQuoteProps {
    hasValidPart: boolean
    orderMarginPercentage: number
    toContactExtended?: ContactViewRow
    quoteUser?: QuoteUserInfo
    warehouse?: WarehouseReturnPayload
    reviewingQuoteOutput: boolean
    currentTaxCertificates?: CustomerTaxCertificate[] | null
    expiredTaxCertificates?: CustomerTaxCertificate[] | null
}

export const OrderScreenHeaderReviewQuote = (props: OrderScreenHeaderReviewQuoteProps) => {
    const {
        hasValidPart,
        toContactExtended,
        quoteUser,
        warehouse,
        currentTaxCertificates,
        expiredTaxCertificates,
    } = props

    let orderShipTo = useAppSelector((state) => state.order.currentShipTo)
    let currentAccount = useAppSelector((state) => state.order.currentAccount!)
    let toContact = useAppSelector((state) => state.order.toContact)
    const orderHeader = useAppSelector((state) => state.order.orderHeader)
    const orderMiscInfo = useAppSelector((state) => state.order.orderMiscInfo!)
    // const showImages = useAppSelector((state) => state.order.showImages)

    const dispatch = useAppDispatch()
    const classes = useStyles()

    const referenceNumber = useAppSelector((state) => state.order.orderHeader[0]?.RFQNumber || '')
    const webSubmitOrderURL = useAppSelector(
        (state) => state.order.orderHeader[0]?.WebSubmitOrderURL || ''
    )

    const [attachmentModalOpen, setAttachmentModalOpen] = useState(false)
    const orderFilenames = useAppSelector((state) => state.order.orderFilenames)
    const [sendQuoteOpen, setSendQuoteOpen] = useState(false)
    const [requestQuoteChangesOpen, setRequestQuoteChangesOpen] = useState(false)
    const [downloadingPDF, setDownloadingPDF] = useState(false)
    const [logoUrl, setLogoUrl] = useState('')
    const [logoWidth, setLogoWidth] = useState('')
    const [companyDisplayName, setCompanyDisplayName] = useState<string | undefined>()

    const shippingInfo = useAppSelector((state) => state.order.shippingInfo)
    const customerMode = useAppSelector((state) => state.meta.customerMode)
    const previewMode = useAppSelector((state) => state.meta.previewMode)

    const {
        carrier: shippingCarrier,
        service: shippingService,
        terms: shippingTerms,
        collectAccount: shippingCollectAccount,
    } = shippingInfo

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })
    const [shipToRef, bounds] = useMeasure()

    useEffect(() => {
        const determineAndSetCompanyLogo = async () => {
            const currentUser = await getCurrentUser()
            const company = currentUser.CompanyCode
            let url = 'https://ww2.awc-inc.com/webimages/AWCLogoWithTagline.svg'
            let width = '241px'
            if (company === 'C') {
                url = 'https://www.a3im.com/WebImages/A3IM/a3imlogo.png'
                width = '140px'
            } else if (company === 'E') {
                url = 'https://ww2.awc-inc.com/webimages/acelogo.jpg'
                width = '270px'
            }
            setLogoUrl(url)
            setLogoWidth(width)
        }
        determineAndSetCompanyLogo()

        setCompanyDisplayName(
            orderShipTo?.ShipToName && orderShipTo?.ShipToName > ''
                ? orderShipTo?.ShipToName
                : orderShipTo?.BillToName && orderShipTo?.BillToName > ''
                ? orderShipTo?.BillToName
                : undefined
        )
    }, [])

    return (
        <div className={styles.reviewScreenHeader}>
            <div
                className={clsx([
                    styles.reviewOrderHeaderContainer,
                    (previewMode || customerMode) && styles.previewMode,
                ])}
            >
                {(previewMode || customerMode) && (
                    <div className={styles.logoConfidentialShippingSection}>
                        {/* LOGO SECTION */}
                        {previewMode && (
                            <div className={styles.logoSection}>
                                <img src={logoUrl} style={{ width: logoWidth }} />
                            </div>
                        )}
                        {(previewMode || customerMode) && (
                            <div className={styles.confidentialSection}>
                                <div>
                                    <span>Confidential:</span> To be used by recipient's
                                    organization only
                                </div>
                            </div>
                        )}
                        {previewMode && (
                            <div className={styles.shippingMethodReviewQuote}>
                                <div>
                                    <div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteBoldLarge
                                                    : styles.shippingMethodReviewQuoteBold
                                            }
                                            style={{ fontWeight: '300' }}
                                        >
                                            Shipping Method:
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 5 }}>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteBoldLarge
                                                    : styles.shippingMethodReviewQuoteBold
                                            }
                                        >
                                            Carrier:&nbsp;
                                        </div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteValueLarge
                                                    : styles.shippingMethodReviewQuoteValue
                                            }
                                        >
                                            {shippingCarrier}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteBoldLarge
                                                    : styles.shippingMethodReviewQuoteBold
                                            }
                                        >
                                            Service:&nbsp;
                                        </div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteValueLarge
                                                    : styles.shippingMethodReviewQuoteValue
                                            }
                                        >
                                            {shippingService}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteBoldLarge
                                                    : styles.shippingMethodReviewQuoteBold
                                            }
                                        >
                                            Terms:&nbsp;
                                        </div>
                                        <div
                                            className={
                                                isLargeView
                                                    ? styles.shippingMethodReviewQuoteValueLarge
                                                    : styles.shippingMethodReviewQuoteValue
                                            }
                                        >
                                            {shippingTerms}
                                        </div>
                                    </div>
                                    {shippingCollectAccount !== '' && (
                                        <div>
                                            <div
                                                className={
                                                    isLargeView
                                                        ? styles.shippingMethodReviewQuoteBoldLarge
                                                        : styles.shippingMethodReviewQuoteBold
                                                }
                                            >
                                                Account #:&nbsp;
                                            </div>
                                            <div
                                                className={
                                                    isLargeView
                                                        ? styles.shippingMethodReviewQuoteValueLarge
                                                        : styles.shippingMethodReviewQuoteValue
                                                }
                                            >
                                                {shippingCollectAccount}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* TO SECTION */}
                {(toContactExtended || companyDisplayName) && (
                    <div className={styles.toSection}>
                        {toContactExtended ? (
                            <>
                                <div style={{ fontWeight: 300, marginBottom: '0.4rem' }}>To:</div>
                                <div style={{ whiteSpace: 'break-spaces' }}>
                                    {toContact?.FirstName.trim()} {toContact?.LastName.trim()}
                                </div>
                                <div style={{ fontWeight: 300, overflowWrap: 'break-word' }}>
                                    {toContactExtended.CompanyName}
                                </div>
                                <div style={{ color: 'var(--linkblue)' }}>
                                    {toContact?.EmailAddress}
                                </div>
                                <div style={{ fontWeight: 300 }}>
                                    {toContactExtended.TelephoneNumber}
                                </div>
                            </>
                        ) : (
                            companyDisplayName && (
                                <>
                                    <div style={{ fontWeight: 300, marginBottom: '0.4rem' }}>
                                        To:
                                    </div>
                                    <div style={{ fontWeight: 300, overflowWrap: 'break-word' }}>
                                        {orderShipTo?.ShipToName && orderShipTo?.ShipToName > ''
                                            ? orderShipTo?.ShipToName
                                            : orderShipTo?.BillToName &&
                                              orderShipTo?.BillToName > ''
                                            ? orderShipTo?.BillToName
                                            : ''}
                                    </div>
                                </>
                            )
                        )}
                    </div>
                )}
                {/* <FROMSECTION/> */}
                <div className={styles.fromSection}>
                    <div style={{ fontWeight: 300, marginBottom: '0.4rem' }}>From:</div>
                    <div className={styles.reviewQuotedBySection}>
                        <div>
                            {quoteUser?.Firstname.trim()} {quoteUser?.Lastname.trim()}
                        </div>
                        <div>{warehouse?.name}</div>
                        <div style={{ color: 'var(--linkblue)' }}>{quoteUser?.Email}</div>
                        <div style={{ fontWeight: 300 }}>
                            {quoteUser?.TelephoneNumber ? quoteUser?.TelephoneNumber : ''}
                        </div>
                    </div>
                </div>
                {/* METAINFOSECTION */}
                <div className={styles.reviewQuoteMetaInfo2}>
                    <div>Reference:</div>
                    <div>
                        <div
                            className={
                                isLargeView && !previewMode
                                    ? styles.lightTextLarge
                                    : styles.lightTextSmall
                            }
                            style={{
                                // paddingRight: 100,
                                maxWidth: 370,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                            }}
                            data-testid='referenceNumber'
                        >
                            {referenceNumber || ''}
                        </div>
                    </div>
                    <div>Quote Date:</div>
                    <div>
                        <div
                            className={
                                isLargeView && !previewMode
                                    ? styles.lightTextLarge
                                    : styles.lightTextSmall
                            }
                            style={{
                                // paddingRight: 100,
                                maxWidth: 370,
                                overflow: 'hidden',
                            }}
                        >
                            {previewMode && orderHeader[0].OrderDate
                                ? new Date(orderHeader[0].OrderDate).toLocaleDateString()
                                : new Date().toLocaleDateString()}
                        </div>
                    </div>
                    <div>Valid Until:</div>
                    <div>
                        <div
                            className={
                                isLargeView && !previewMode
                                    ? styles.lightTextLarge
                                    : styles.lightTextSmall
                            }
                            style={{
                                // paddingRight: 100,
                                maxWidth: 370,
                                overflow: 'hidden',
                            }}
                        >
                            {orderHeader[0].QuoteExpirationDate
                                ? new Date(orderHeader[0].QuoteExpirationDate).toLocaleDateString()
                                : ''}
                        </div>
                    </div>
                    <div>AWC Quote #:</div>
                    <div
                        className={
                            isLargeView && !previewMode
                                ? styles.lightTextLarge
                                : styles.lightTextSmall
                        }
                        data-testid='quoteNumber'
                    >
                        {orderHeader[0].OrderNumber === '9999999999'
                            ? 'New'
                            : orderHeader[0].OrderNumber}
                    </div>
                    <div>Quote Version:</div>
                    <div
                        className={
                            isLargeView && !previewMode
                                ? styles.lightTextLarge
                                : styles.lightTextSmall
                        }
                        data-testid='quoteVersion'
                    >
                        {(orderHeader[0].Revision || 0).toString().padStart(3, '0')}
                    </div>
                    <div>AWC Account #:</div>
                    <div
                        className={
                            isLargeView && !previewMode
                                ? styles.lightTextLarge
                                : styles.lightTextSmall
                        }
                    >
                        {orderHeader[0].CustomerAccountCode}
                    </div>
                </div>
                <div className={styles.addressSection}>
                    <div className={styles.billToSection}>
                        <div className={styles.billToRow}>
                            <div
                                className={`${styles.lightTextSmall} ${styles.billToLineReviewQuote}`}
                            >
                                Bill to:
                            </div>
                        </div>
                        <div className={styles.customerLineReviewQuote}>
                            {orderHeader[0].BillToName || currentAccount.BillToName} (
                            {orderHeader[0].CustomerAccountCode.trim()})
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderHeader[0].BillToAddress1 || currentAccount.BillToAddress1}
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderHeader[0].BillToAddress2 || currentAccount.BillToAddress2}
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderHeader[0].BillToCity || currentAccount.BillToCity}
                            {orderHeader[0].BillToCity || (currentAccount.BillToCity && ',')}{' '}
                            {orderHeader[0].BillToState || currentAccount.BillToState}{' '}
                            {orderHeader[0].BillToZipcode || currentAccount.BillToZipcode}
                        </div>
                        <div className={styles.billToLineReviewQuote} style={{ paddingBottom: 10 }}>
                            {orderHeader[0].BillToCountry
                                ? COUNTRY_CODES[orderHeader[0].BillToCountry.trim()]
                                : currentAccount.BillToCountry
                                ? COUNTRY_CODES[currentAccount.BillToCountry.trim()]
                                : ''}
                        </div>
                    </div>
                    <div className={styles.shipToSection}>
                        <div
                            className={styles.shipToRow}
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                            <div
                                className={`${styles.lightTextSmall} ${styles.billToLineReviewQuote}`}
                            >
                                Ship to:
                            </div>
                            <div></div>
                        </div>
                        <div ref={shipToRef} className={styles.customerLineReviewQuote}>
                            <TextOverflow
                                text={orderShipTo?.ShipToName || ''}
                                maxWidth={bounds.width}
                            />
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderShipTo?.ShipToAddress1 || ''}
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderShipTo?.ShipToAddress2 || ''}
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {orderShipTo?.ShipToCity || ''}
                            {orderShipTo?.ShipToCity && ','} {orderShipTo?.ShipToState || ''}{' '}
                            {orderShipTo?.ShipToZipcode || ''}
                        </div>
                        <div className={styles.billToLineReviewQuote}>
                            {
                                COUNTRY_CODES[
                                    orderShipTo?.ShipToCountry
                                        ? orderShipTo.ShipToCountry.trim()
                                        : ''
                                ]
                            }
                        </div>
                    </div>
                    {!previewMode && (
                        <div className={styles.taxCertificateSection}>
                            <div
                                className={styles.lightTextSmall}
                                style={{ marginTop: 30, marginBottom: 10 }}
                            >
                                Tax Certificate:
                            </div>
                            {(currentTaxCertificates && currentTaxCertificates.length > 0) ||
                            (expiredTaxCertificates && expiredTaxCertificates.length > 0) ? (
                                <>
                                    <div className={styles.customerLine}>
                                        {currentTaxCertificates &&
                                        currentTaxCertificates?.length > 0 ? (
                                            <HoverIcon
                                                growLeft={true}
                                                growDown={true}
                                                renderIcon={() => (
                                                    <span className={styles.taxCertificateNumber}>
                                                        Current ({currentTaxCertificates?.length})
                                                    </span>
                                                )}
                                            >
                                                {(closeBubble: () => void) => (
                                                    <div
                                                        className={styles.taxBubble}
                                                        style={{
                                                            height: '100%',
                                                            textAlign: 'left',
                                                            whiteSpace: 'nowrap',
                                                            padding: '14px 19px',
                                                        }}
                                                        onClick={(e: any) => e.stopPropagation()}
                                                    >
                                                        <div>
                                                            <span
                                                                style={{
                                                                    fontWeight: 400,
                                                                    marginRight: 12,
                                                                    marginBottom: 15,
                                                                }}
                                                            >
                                                                Avalara Stored Exemption
                                                                Certificates
                                                            </span>
                                                        </div>

                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    '110px 110px 120px 135px 135px 130px',
                                                                fontWeight: 500,
                                                                justifyItems: 'center',
                                                                marginTop: 10,
                                                            }}
                                                        >
                                                            <div>Cert Tax ID</div>
                                                            <div>Exposure Zone</div>
                                                            <div>Exempt Reason</div>
                                                            <div>Certificate Eff Date</div>
                                                            <div>Certificate Exp Date</div>
                                                            <div>PO# (1 Time Use)</div>
                                                        </div>

                                                        <div
                                                            className='a-divider'
                                                            style={{
                                                                marginTop: 10,
                                                                marginBottom: 10,
                                                            }}
                                                        />

                                                        <Scrollbars
                                                            className={'scroll-bar'}
                                                            renderTrackHorizontal={() => <div />}
                                                            autoHeight
                                                            autoHeightMin={0}
                                                            autoHeightMax={210}
                                                            // height={210}
                                                        >
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns:
                                                                            '120px 110px 100px 150px 130px 125px',
                                                                        paddingRight: 15,
                                                                    }}
                                                                >
                                                                    {currentTaxCertificates.map(
                                                                        (
                                                                            row: CustomerTaxCertificate
                                                                        ) => (
                                                                            <>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {row.TaxID}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        row.ExposureZone
                                                                                    }
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        row.ExemptReason
                                                                                    }
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {new Date(
                                                                                        row.EffectiveDate
                                                                                    ).toLocaleDateString(
                                                                                        'en-US',
                                                                                        {
                                                                                            month: '2-digit',
                                                                                            day: '2-digit',
                                                                                            year: 'numeric',
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {new Date(
                                                                                        row.ExpirationDate
                                                                                    ).toLocaleDateString(
                                                                                        'en-US',
                                                                                        {
                                                                                            month: '2-digit',
                                                                                            day: '2-digit',
                                                                                            year: 'numeric',
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {row.PONumber}
                                                                                </div>
                                                                            </>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </>
                                                        </Scrollbars>
                                                    </div>
                                                )}
                                            </HoverIcon>
                                        ) : (
                                            <span>Current (0)</span>
                                        )}
                                    </div>
                                    <div className={styles.customerLine}>
                                        {expiredTaxCertificates &&
                                        expiredTaxCertificates?.length > 0 ? (
                                            <HoverIcon
                                                growLeft={true}
                                                growDown={true}
                                                renderIcon={() => (
                                                    <span className={styles.taxCertificateNumber}>
                                                        Expired ({expiredTaxCertificates?.length})
                                                    </span>
                                                )}
                                            >
                                                {(closeBubble: () => void) => (
                                                    <div
                                                        className={styles.taxBubble}
                                                        style={{
                                                            height: '100%',
                                                            textAlign: 'left',
                                                            whiteSpace: 'nowrap',
                                                            padding: '14px 19px',
                                                        }}
                                                        onClick={(e: any) => e.stopPropagation()}
                                                    >
                                                        <div>
                                                            <span
                                                                style={{
                                                                    fontWeight: 400,
                                                                    marginRight: 12,
                                                                    marginBottom: 15,
                                                                }}
                                                            >
                                                                Avalara Stored Exemption
                                                                Certificates
                                                            </span>
                                                        </div>

                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    '110px 110px 120px 135px 135px 130px',
                                                                fontWeight: 500,
                                                                justifyItems: 'center',
                                                                marginTop: 10,
                                                            }}
                                                        >
                                                            <div>Cert Tax ID</div>
                                                            <div>Exposure Zone</div>
                                                            <div>Exempt Reason</div>
                                                            <div>Certificate Eff Date</div>
                                                            <div>Certificate Exp Date</div>
                                                            <div>PO# (1 Time Use)</div>
                                                        </div>

                                                        <div
                                                            className='a-divider'
                                                            style={{
                                                                marginTop: 10,
                                                                marginBottom: 10,
                                                            }}
                                                        />

                                                        <Scrollbars
                                                            className={'scroll-bar'}
                                                            renderTrackHorizontal={() => <div />}
                                                            autoHeight
                                                            autoHeightMin={0}
                                                            autoHeightMax={210}
                                                            // height={210}
                                                        >
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns:
                                                                            '120px 110px 100px 150px 130px 125px',
                                                                        paddingRight: 15,
                                                                    }}
                                                                >
                                                                    {expiredTaxCertificates.map(
                                                                        (
                                                                            row: CustomerTaxCertificate
                                                                        ) => (
                                                                            <>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {row.TaxID}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        row.ExposureZone
                                                                                    }
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        row.ExemptReason
                                                                                    }
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {new Date(
                                                                                        row.EffectiveDate
                                                                                    ).toLocaleDateString(
                                                                                        'en-US',
                                                                                        {
                                                                                            month: '2-digit',
                                                                                            day: '2-digit',
                                                                                            year: 'numeric',
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'center',
                                                                                    }}
                                                                                >
                                                                                    {new Date(
                                                                                        row.ExpirationDate
                                                                                    ).toLocaleDateString(
                                                                                        'en-US',
                                                                                        {
                                                                                            month: '2-digit',
                                                                                            day: '2-digit',
                                                                                            year: 'numeric',
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                                <div
                                                                                    style={{
                                                                                        textAlign:
                                                                                            'start',
                                                                                    }}
                                                                                >
                                                                                    {row.PONumber}
                                                                                </div>
                                                                            </>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </>
                                                        </Scrollbars>
                                                    </div>
                                                )}
                                            </HoverIcon>
                                        ) : (
                                            <span>Expired (0)</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className={styles.customerLine}>Not on File</div>
                            )}
                        </div>
                    )}
                </div>
                <div
                    className={clsx([
                        // !hasValidPart && styles.fadedText,
                        styles.reviewQuoteReviewContainer,
                    ])}
                >
                    {!previewMode && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'end',
                                padding: '0px 0px 10px 0px',
                            }}
                        >
                            <div onClick={() => setAttachmentModalOpen(true)}>
                                <div
                                    style={{
                                        color: 'var(--linkblue)',
                                        fontSize: 14,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Attached Documents ({orderFilenames.length})
                                </div>
                            </div>
                        </div>
                    )}
                    {!customerMode && !previewMode && (
                        <AWCButton
                            type={BUTTON_TYPES.PRIMARY}
                            style={{
                                marginBottom: '5%',
                                width: '100%',
                                boxSizing: 'border-box',
                                height: 40,
                            }}
                            onClick={() => setSendQuoteOpen(true)}
                        >
                            Send Quote
                        </AWCButton>
                    )}
                    {customerMode &&
                        orderHeader[0].Restrictions?.trim() !== '4' &&
                        orderHeader[0].Restrictions?.trim() !== '5' && (
                            <AWCButton
                                type={BUTTON_TYPES.PRIMARY}
                                style={{
                                    marginBottom: '5%',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    height: 40,
                                }}
                                onClick={() => (window.location.href = webSubmitOrderURL)}
                            >
                                Submit Order
                            </AWCButton>
                        )}
                    {!customerMode && (
                        <AWCButton
                            type={BUTTON_TYPES.SECONDARY}
                            style={{
                                marginBottom: '5%',
                                width: '100%',
                                boxSizing: 'border-box',
                                height: 40,
                            }}
                            onClick={async () => {
                                setDownloadingPDF(true)

                                let prom = !previewMode ? await dispatch(saveOrder(null)) : null

                                if (previewMode || prom?.meta.requestStatus === 'fulfilled')
                                    await dispatch(downloadPDF(null))

                                setDownloadingPDF(false)
                            }}
                        >
                            Download PDF
                        </AWCButton>
                    )}
                    {customerMode &&
                        orderHeader[0].Restrictions?.trim() !== '4' &&
                        orderHeader[0].Restrictions?.trim() !== '5' && (
                            <AWCButton
                                type={BUTTON_TYPES.SECONDARY}
                                style={{
                                    marginBottom: '5%',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    height: 40,
                                }}
                                onClick={() => setRequestQuoteChangesOpen(true)}
                            >
                                Request Changes
                            </AWCButton>
                        )}
                    {!previewMode && (
                        <>
                            <ShippingMethodInfo allowEdit={true} isLargeView={isLargeView} />
                            <div
                                className={styles.divider}
                                style={{ marginBottom: '8%', marginTop: 38 }}
                            />
                        </>
                    )}
                    <div className={styles.moneyLines}>
                        <div>Subtotal:</div>
                        {orderMiscInfo && hasValidPart ? (
                            <>
                                <div></div>
                                <div>
                                    <span>$</span>
                                    {orderMiscInfo.OrderValue.SubTotal.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </div>
                            </>
                        ) : (
                            <>
                                <div></div>
                                <div></div>
                            </>
                        )}
                        <div>Est. Freight:</div>

                        {orderMiscInfo && hasValidPart ? (
                            orderMiscInfo.OrderValue.FreightErrorMessage ? (
                                <>
                                    <div></div>
                                    {customerMode || previewMode ? (
                                        <div style={{ fontWeight: 300 }}>TBD</div>
                                    ) : (
                                        <HoverIcon
                                            growLeft
                                            growUp
                                            renderIcon={() => (
                                                <div style={{ fontWeight: 300 }}>
                                                    TBD&nbsp;
                                                    <span className={'a-i-info-small'}></span>
                                                </div>
                                            )}
                                        >
                                            {(closeBubble: () => void) =>
                                                orderMiscInfo.OrderValue.FreightErrorMessage && (
                                                    <div
                                                        className={styles.identifierBubble}
                                                        style={{ width: 270 }}
                                                    >
                                                        <div>
                                                            {
                                                                orderMiscInfo.OrderValue
                                                                    .FreightErrorMessage
                                                            }
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </HoverIcon>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div></div>
                                    <div>
                                        <span>$</span>
                                        {orderMiscInfo.OrderValue.FreightEstimate.toFixed(2)}
                                    </div>
                                </>
                            )
                        ) : (
                            <>
                                <div></div>
                                <div></div>
                            </>
                        )}

                        {/* creditlimit === 0 => no tax cert */}
                        <div>Est. Tax:</div>
                        {orderMiscInfo && hasValidPart ? (
                            orderHeader[0].TaxAuthorityCode === 'TEMP' ? (
                                <>
                                    <div></div>
                                    <div style={{ fontWeight: 300 }}>See Note 1</div>
                                </>
                            ) : (
                                <>
                                    <div></div>
                                    <div>
                                        <span>$</span>
                                        {orderMiscInfo.OrderValue.TaxAmt.toFixed(2)}
                                    </div>
                                </>
                            )
                        ) : (
                            <>
                                <div></div>
                                <div></div>
                            </>
                        )}
                    </div>
                    <div className={styles.divider} style={{ margin: '9px 0px' }} />
                    <div
                        className={styles.orderTotal}
                        style={{
                            fontSize: 14,
                            fontWeight: 500,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        {previewMode ? 'Quote' : 'Order'} Total:{' '}
                        <span>
                            {orderMiscInfo && hasValidPart
                                ? orderMiscInfo.OrderValue.Total.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                  })
                                : '$0.00'}
                        </span>
                    </div>
                </div>
            </div>
            <FastQuoteAttachmentModal
                open={attachmentModalOpen}
                close={() => setAttachmentModalOpen(false)}
                reviewingQuoteOutput={true}
            />
            <Modal
                open={downloadingPDF}
                className={classes.modal}
                onClose={() => {}}
                BackdropComponent={Backdrop}
            >
                <div className={'centered'}>
                    <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                </div>
            </Modal>
            {sendQuoteOpen && <SendQuoteModal close={() => setSendQuoteOpen(false)} />}
            {requestQuoteChangesOpen && (
                <RequestQuoteChangesModal close={() => setRequestQuoteChangesOpen(false)} />
            )}
        </div>
    )
}
